import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import {
  FeFilterPreset,
  FilterGroupingEnum,
  SortDirectionEnum,
} from 'graphql-common';
import { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import ChipWithDropDown from '@lib/components/ChipWithDropDown/ChipWithDropDown';
import IconButton, {
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import Button from '@lib/components/Button/Button';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import ConfirmationModal from '@lib/components/ConfirmationModal/ConfirmationModal';
import { IconName } from '@lib/components/Modal/enums';
import Trans from '@lib/components/Trans/Trans';
import toast from '@lib/components/Toast/Toast';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import { PER_PAGE_KEY_DEFAULT } from '@lib/enums/urls';
import { MonitoringEntity } from '../enums';
import useMutations from '../utils/useMutations';
import { UseQueryResult } from '../utils/useQueries';
import styles from './ApplyFilterPreset.module.scss';
import getFeFilterPresetKind from '../utils/getFeFilterPresetKind';

type PopoverContentProps = {
  filterPresetId?: string;
  handleApply: (handleClose: () => void) => void;
  handleClose: () => void;
  handleDeleteClick: () => void;
  isInitialLoading: boolean;
  isDeleting: boolean;
  isFetching: boolean;
  items: FeFilterPreset[];
  listRef: React.RefObject<HTMLDivElement | null>;
  onCreateClick: () => void;
  onMount: () => void;
  onUnmount: () => void;
  onScroll: () => void;
  onToggleItem: (id: string) => void;
  selectedId: string | null;
  showGradient: boolean;
  t: TFunction<'translation', undefined>;
};

function ApplyFilterPresetPopoverContent(props: PopoverContentProps) {
  const {
    filterPresetId,
    handleApply,
    handleClose,
    handleDeleteClick,
    isInitialLoading,
    isDeleting,
    isFetching,
    items,
    listRef,
    onCreateClick,
    onMount,
    onUnmount,
    onScroll,
    onToggleItem,
    selectedId,
    showGradient,
    t,
  } = props;

  useEffect(() => {
    onMount();
    return () => {
      onUnmount();
    };
  }, [onMount, onUnmount]);

  return (
    <div className={styles.container}>
      <div className={styles.createButton}>
        <button
          type="button"
          className={classNames(styles.item, styles.itemWithLeftIcon)}
          onClick={() => {
            handleClose();
            onCreateClick();
          }}
        >
          <MaterialIcon
            icon="save"
            className={styles.itemLeftIcon}
            symbolsOutlined
          />
          <span className={styles.itemName}>{t('save-current-view')}</span>
        </button>
      </div>
      {isInitialLoading ? null : (
        <>
          <div className={styles.list}>
            <div
              className={styles.listScroll}
              ref={listRef}
              onScroll={onScroll}
            >
              {items.map((item) => {
                const isActive = item.id === selectedId;
                const isSelectedByFilter =
                  filterPresetId && filterPresetId === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={classNames(styles.item, {
                      [styles.itemActive]: isActive,
                      [styles.itemSelected]: isSelectedByFilter,
                    })}
                    onClick={() => onToggleItem(item.id)}
                  >
                    <span className={styles.itemName}>{item.name}</span>
                    {filterPresetId && filterPresetId === item.id && (
                      <MaterialIcon
                        className={styles.itemSelectedIcon}
                        icon="star"
                        size="md-24"
                      />
                    )}
                  </button>
                );
              })}
              {isFetching && (
                <div className={styles.loaderRow}>
                  <CircleLoader size={20} padding={8} />
                </div>
              )}
            </div>
            {showGradient && <span className={styles.gradient} />}
          </div>
          {items?.length ? (
            <div className={styles.actions}>
              <IconButton
                icon="delete"
                type={IconButtonTypes.SecondaryFilled}
                disabled={!selectedId}
                isLoading={isDeleting}
                onClick={handleDeleteClick}
                symbolsOutlined
                rounded
              />
              <Button
                buttonText={t('apply')}
                disabled={!selectedId}
                onClick={() => handleApply(handleClose)}
                leftIcon="check"
              />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

type Props = {
  filterPresetId?: string;
  lastUpdate: string;
  monitoringEntity: MonitoringEntity;
  onApplyCallback: (filterPreset: FeFilterPreset) => void;
  onCreateClick: () => void;
  onDeletePresetCallback: (filterPresetId: string) => void;
  useQueriesResult: UseQueryResult;
};

export default function ApplyFilterPreset({
  lastUpdate,
  onApplyCallback,
  onDeletePresetCallback,
  filterPresetId,
  monitoringEntity,
  onCreateClick,
  useQueriesResult,
}: Props) {
  const { t } = useTranslation();

  const listRef = useRef<HTMLDivElement>(null);
  const fetchIdRef = useRef(0);
  const isFetchingRef = useRef(false);
  const shouldReloadRef = useRef(true);

  const { fetchFeFilterPresetDestroyMutation } = useMutations();
  const { fetchFeFilterPresetsLazyQuery } = useQueriesResult;

  // State
  const [items, setItems] = useState<FeFilterPreset[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isFetching, setIsFetching] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showGradient, setShowGradient] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const previousEntityRef = useRef<MonitoringEntity | null>(null);

  const hasMore = useMemo(() => page < totalPages, [page, totalPages]);
  const isInitialLoading = isFetching && items.length === 0;

  // Actions
  const loadPage = useCallback(
    async (nextPage: number) => {
      const isReset = nextPage === 1;
      if (isFetchingRef.current && !isReset) {
        return;
      }

      fetchIdRef.current += 1;
      const fetchId = fetchIdRef.current;

      if (isReset) {
        setSelectedId(null);
        setPage(0);
        setTotalPages(0);
      }

      isFetchingRef.current = true;
      setIsFetching(true);

      const kind = getFeFilterPresetKind(monitoringEntity);

      try {
        const res = await fetchFeFilterPresetsLazyQuery({
          variables: {
            page: nextPage,
            limit: +PER_PAGE_KEY_DEFAULT,
            sorts: { createdAt: SortDirectionEnum.Asc },
            filters: [
              {
                kind: {
                  grouping: FilterGroupingEnum.Or,
                  predicate: {
                    eq: kind,
                  },
                },
              },
            ],
          },
        });

        if (fetchId !== fetchIdRef.current) {
          return; // Stale request, ignore
        }

        const collection = (res.data?.data?.collection ||
          []) as FeFilterPreset[];
        const metadata = res.data?.data?.metadata;
        if (isReset) {
          setItems(collection);
        } else {
          setItems((prev) => [...prev, ...collection]);
        }
        if (metadata) {
          setPage(metadata.currentPage || nextPage);
          setTotalPages(metadata.totalPages || 0);
        } else {
          // Fallback if no metadata provided
          setPage(nextPage);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch filter presets', e);
      } finally {
        if (fetchId === fetchIdRef.current) {
          isFetchingRef.current = false;
          setIsFetching(false);
        }
      }
    },
    [fetchFeFilterPresetsLazyQuery, monitoringEntity],
  );

  const handleDeleteClick = useCallback(() => {
    if (!selectedId) return;
    setIsDeleteModalOpen(true);
  }, [selectedId]);

  const handleDestroy = useCallback(async () => {
    if (!selectedId) return;
    setIsDeleting(true);
    fetchFeFilterPresetDestroyMutation({
      variables: { id: selectedId },
      onCompleted: () => {
        setIsDeleteModalOpen(false);
        toast({ content: t('destroy-filter-preset-success') });
        setItems((prev) => prev.filter((it) => it.id !== selectedId));
        setSelectedId(null);
        setIsDeleting(false);
        onDeletePresetCallback(selectedId);
      },
      onError: (error) => {
        setIsDeleting(false);
        showGraphQLErrors(error, t);
      },
    });
  }, [
    fetchFeFilterPresetDestroyMutation,
    selectedId,
    t,
    onDeletePresetCallback,
  ]);

  const handleCloseDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(false);
  }, []);

  const handleConfirmedDelete = () => {
    if (selectedId) {
      handleDestroy();
    }
  };

  const handleApply = useCallback(
    (handleClose: () => void) => {
      const selected = items.find((it) => it.id === selectedId);
      if (selected) {
        onApplyCallback(selected);
      }
      handleClose();
    },
    [items, onApplyCallback, selectedId],
  );
  const handleScrollCheck = useCallback(() => {
    const el = listRef.current;
    if (!el) return;

    const ITEM_MIN_HEIGHT = 56;
    const MAX_VISIBLE_HEIGHT = 255;
    const hasScrollableContent =
      items.length * ITEM_MIN_HEIGHT > MAX_VISIBLE_HEIGHT;
    const hasScrollPosition =
      el.scrollTop + el.clientHeight < el.scrollHeight - 2;

    setShowGradient(hasScrollableContent && hasScrollPosition);
  }, [items.length]);

  const onScroll = useCallback(() => {
    const el = listRef.current;
    if (!el || isFetching) return;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8; // threshold
    if (nearBottom && hasMore) {
      loadPage(page + 1);
    }
    handleScrollCheck();
  }, [hasMore, isFetching, loadPage, page, handleScrollCheck]);

  const ensureFirstPageLoaded = useCallback(() => {
    if (isFetchingRef.current) {
      return;
    }
    shouldReloadRef.current = false;
    loadPage(1);
  }, [loadPage]);

  useEffect(() => {
    if (
      previousEntityRef.current !== null &&
      previousEntityRef.current !== monitoringEntity
    ) {
      shouldReloadRef.current = true;
      fetchIdRef.current += 1;
      isFetchingRef.current = false;
      setIsFetching(false);
      setSelectedId(null);
      setPage(0);
      setTotalPages(0);
      setItems([]);
      setShowGradient(false);
      if (isDropdownOpen) {
        ensureFirstPageLoaded();
      }
    }
    previousEntityRef.current = monitoringEntity;
  }, [monitoringEntity, ensureFirstPageLoaded, isDropdownOpen]);

  const lastUpdateRef = useRef(lastUpdate);
  useEffect(() => {
    // Only mark stale and refetch if lastUpdate actually changed
    if (lastUpdateRef.current === lastUpdate) {
      return;
    }
    lastUpdateRef.current = lastUpdate;

    // Mark data stale when presets updated; fetch immediately only if open
    shouldReloadRef.current = true;
    if (isDropdownOpen && !isFetchingRef.current) {
      ensureFirstPageLoaded();
    }
    // Intentionally exclude isDropdownOpen to avoid double fetch on open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastUpdate, ensureFirstPageLoaded]);

  // This effect is only needed to handle edge case when dropdown opens while data is stale
  // It should NOT re-trigger after a successful fetch
  const prevIsFetchingRef = useRef(isFetching);
  useEffect(() => {
    const wasFetching = prevIsFetchingRef.current;
    prevIsFetchingRef.current = isFetching;

    // Only trigger if:
    // 1. We're not currently fetching
    // 2. Dropdown is open
    // 3. Data is marked as stale
    // 4. We weren't just fetching (to avoid re-triggering after fetch completes)
    if (
      !isFetching &&
      isDropdownOpen &&
      shouldReloadRef.current &&
      !wasFetching
    ) {
      ensureFirstPageLoaded();
    }
  }, [isFetching, isDropdownOpen, ensureFirstPageLoaded]);

  useEffect(() => {
    setTimeout(() => {
      handleScrollCheck();
    }, 0);
  }, [items, handleScrollCheck]);

  useEffect(() => {
    window.addEventListener('resize', handleScrollCheck);
    return () => window.removeEventListener('resize', handleScrollCheck);
  }, [handleScrollCheck]);

  const selectedPreset = useMemo(
    () => items.find((item) => item.id === selectedId),
    [items, selectedId],
  );

  const handleToggleItem = useCallback((id: string) => {
    setSelectedId((prev) => (prev === id ? null : id));
  }, []);

  const handlePopoverMount = useCallback(() => {
    setIsDropdownOpen(true);
    // Reset all list-related state to defaults on every open
    fetchIdRef.current += 1;
    isFetchingRef.current = false;
    setIsFetching(false);
    setSelectedId(null);
    setPage(0);
    setTotalPages(0);
    setItems([]);
    setShowGradient(false);
    ensureFirstPageLoaded();
  }, [ensureFirstPageLoaded]);

  const handlePopoverUnmount = useCallback(() => {
    setIsDropdownOpen(false);
  }, []);

  const renderPopoverBody = useCallback(
    ({ handleClose: handleCloseFn }) => (
      <ApplyFilterPresetPopoverContent
        filterPresetId={filterPresetId}
        handleApply={handleApply}
        handleClose={handleCloseFn}
        handleDeleteClick={handleDeleteClick}
        isInitialLoading={isInitialLoading}
        isDeleting={isDeleting}
        isFetching={isFetching}
        items={items}
        listRef={listRef}
        onCreateClick={onCreateClick}
        onMount={handlePopoverMount}
        onUnmount={handlePopoverUnmount}
        onScroll={onScroll}
        onToggleItem={handleToggleItem}
        selectedId={selectedId}
        showGradient={showGradient}
        t={t}
      />
    ),
    [
      filterPresetId,
      handleApply,
      handleDeleteClick,
      isInitialLoading,
      isDeleting,
      isFetching,
      items,
      listRef,
      onCreateClick,
      handlePopoverMount,
      handlePopoverUnmount,
      onScroll,
      handleToggleItem,
      selectedId,
      showGradient,
      t,
    ],
  );

  const leftIconComponent = filterPresetId ? (
    <MaterialIcon
      icon="star"
      size="md-16"
      className={styles.buttonActiveIcon}
    />
  ) : undefined;

  return (
    <>
      <ChipWithDropDown
        body={renderPopoverBody}
        label={t('apply-view')}
        leftIcon="star"
        leftIconComponent={leftIconComponent}
        leftIconSymbolsOutlined
        size={ChipSizes.large}
        trigger="click"
        type={ChipTypes.chipFilter}
        width={240}
      />
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmedDelete}
        title={t('are-you-sure')}
        description={
          <Trans
            i18nKey="filter-preset-delete-text"
            values={{ name: selectedPreset?.name }}
          />
        }
        confirmButtonLabel={t('delete')}
        icon={IconName.Delete}
        loading={isDeleting}
        useProgress
      />
    </>
  );
}
